/**
 * The only place Russian UI copy is allowed to live. Components take `t` from
 * `useT()` instead of writing strings inline, and `kk`/`en` are siblings of
 * this file with the same shape.
 *
 * This dictionary is the shape everyone else is checked against: `Dictionary`
 * is `typeof ru`, so a key missing from a translation is a build error, not a
 * blank label someone notices in production.
 */
import { forms } from "./plural";

export const ru = {
  app: {
    title: "KGS Methane Monitoring",
    subtitle: "Satellite-based methane detection system",
    demoBadge: "ДЕМО",
    demoTitle: "Синтетические демо-данные, не реальные измерения",
  },
  header: {
    themeToggleToLight: "Переключить на светлую тему",
    themeToggleToDark: "Переключить на тёмную тему",
    languageLabel: "Язык интерфейса",
  },
  // Languages are named in themselves, the way language menus always do it:
  // someone looking for Kazakh is looking for "Қазақша", not for whatever the
  // current interface language calls it. So these stay identical in every
  // dictionary — they are here, not in a constant, only because the switcher
  // reads everything else through `t` too.
  languages: {
    ru: "Русский",
    kk: "Қазақша",
    en: "English",
  },
  stats: {
    detections: "Детекций",
    emissions: "кг/ч",
    countLabel: "Считать:",
    modeExtent: "в текущем виде",
    modeCountry: "по всей стране",
    modeExtentHint: "Считаются только детекции в текущем видимом прямоугольнике карты",
    modeCountryHint: "Считаются все детекции по территории РК с учётом фильтров",
  },
  basemap: {
    positron: "Карта (светлая)",
    darkMatter: "Карта (тёмная)",
    satellite: "Спутник",
    satelliteLabels: "Спутник + подписи",
    osm: "OSM стандарт",
    topo: "Рельеф",
  },
  map: {
    resetExtent: "Вся территория",
    fullscreen: "Полноэкранный режим",
    zoomIn: "Увеличить",
    zoomOut: "Уменьшить",
    basemap: "Базовая карта",
    region: "Регион",
    cursorPosition: "Координаты курсора",
    copyCoordinates: "Скопировать координаты",
    coordinatesCopied: "Скопировано",
  },
  layers: {
    title: "Слои",
    open: "Показать панель слоёв",
    collapse: "Свернуть",
    groupBasemap: "Базовая карта",
    groupBorders: "Границы",
    groupData: "Данные мониторинга",
    country: "Границы стран",
    regions: "Границы областей",
    plumes: "Полигоны плюмов",
    raster: "Растровая концентрация",
    rasterOpacity: "Прозрачность растра",
    rasterPending: "Растр появится при выборе эпизода",
    sources: "Точечные источники",
    aoi: "Зоны интереса",
  },
  filters: {
    title: "Фильтры",
    open: "Показать фильтры",
    collapse: "Свернуть",
    period: "Период",
    presetD7: "7 дней",
    presetD30: "30 дней",
    presetD90: "90 дней",
    presetY1: "год",
    presetAll: "весь период",
    satellites: "Спутники",
    minRate: "Минимальный расход, кг/ч",
    apply: "Применить",
    reset: "Сбросить",
    export: "Экспорт данных",
    exportSoon: "Экспорт данных появится на этапе 7",
    chipPeriod: "Период",
    chipMinRate: "Мин. расход",
    removeFilter: "Убрать фильтр",
    changesPlural: forms({
      one: "{n} изменение",
      few: "{n} изменения",
      many: "{n} изменений",
      other: "{n} изменения",
    }),
  },
  satelliteNames: {
    "sentinel-2": "Sentinel-2",
    "landsat-8": "Landsat 8",
    "landsat-9": "Landsat 9",
    emit: "EMIT",
  },
  states: {
    loading: "Загрузка данных…",
    emptyTitle: "Детекций не найдено",
    emptyBody:
      "За выбранными фильтрами ничего не найдено. Расширьте период или снизьте минимальный расход.",
    emptyReset: "Сбросить фильтры",
    errorTitle: "Что-то пошло не так",
    errorBody: "Не удалось загрузить данные мониторинга.",
    errorRetry: "Обновить страницу",
  },
  export: {
    title: "Экспорт данных",
    format: "Формат",
    area: "Область",
    areaExtent: "Текущий экстент",
    areaCountry: "Вся территория",
    fields: "Набор полей",
    fieldsAll: "Все поля",
    fieldsBasic: "Только основные",
    submit: "Скачать",
    cancel: "Отмена",
    close: "Закрыть",
    exporting: "Подготовка…",
    empty: "За текущими фильтрами нет данных для экспорта",
    done: "Выгружено строк:",
    error: "Не удалось выгрузить (для XLSX выполните npm install)",
  },
  detail: {
    episode: "Эпизод",
    close: "Закрыть",
    durationDays: "дней",
    detections: "детекций",
    obsDates: "дат съёмки",
    maxRate: "макс. кг/ч",
    silenceCaveat:
      "Отсутствие детекции между датами не означает, что выброс прекращался: спутник мог просто не наблюдать источник в эти дни или сигнал был подавлен.",
    attributes: "Атрибуты",
    sensors: "Сенсоры",
    extent: "Протяжённость плюма",
    totalMass: "Суммарная масса",
    notCalculated: "не рассчитывается",
    coordinates: "Координаты источника",
    source: "Источник",
    sector: "Сектор",
    operator: "Оператор",
    region: "Область",
    noSource: "не привязан",
    chartTitle: "Динамика расхода",
    chartEmpty: "Нет количественных оценок для графика",
    rasterTitle: "Растровое превью",
    rasterEmpty: "Растровое превью недоступно",
    tableTitle: "Детекции",
    colDate: "Дата",
    colRate: "Расход",
    colConfidence: "Увер.",
    colSatellite: "Спутник",
    download: "Скачать данные по эпизоду",
    downloadSoon: "Экспорт эпизода появится на этапе 7",
    km: "км",
    kg: "кг",
    kgh: "кг/ч",
    notFound: "Эпизод не найден",
    unquantified: "нет оценки",
    noWind: "нет ветра",
    failed: "ошибка",
    meanRate: "Средний расход",
    gapDays: "Окно кластеризации",
    mergedWarn: "Протяжённость велика — возможно, объединены разные источники.",
    sourceEpisodes: "Эпизоды источника",
    noEpisodes: "Нет зарегистрированных эпизодов",
  },
  // Category → sector label. The live AOI layer uses oil_gas / coal_mine /
  // landfill (verified 2026-07-31); the rest are kept for datasets that use the
  // shorter names. sectorLabel() falls back to the raw value if a key is
  // missing, so an unmapped category shows as "coal_mine" rather than blank.
  sectorNames: {
    oil_gas: "Нефть и газ",
    coal_mine: "Уголь",
    coal: "Уголь",
    landfill: "Отходы",
    waste: "Отходы",
    agriculture: "Сельское хозяйство",
    other: "Другое",
  },
  timeline: {
    play: "Воспроизвести",
    pause: "Пауза",
    speed: "Скорость",
    cursor: "Текущая дата",
    window: "Разрыв",
    windowUnit: "дн.",
    windowHint:
      "Сколько дней назад от текущей даты детекции считаются ещё актуальными. Прозрачность кодирует возраст детекции (время), а не силу выброса.",
    noDates: "За выбранный период нет детекций",
    activeEpisodes: forms({
      one: "{n} активный эпизод",
      few: "{n} активных эпизода",
      many: "{n} активных эпизодов",
      other: "{n} активных эпизода",
    }),
  },
  legend: {
    title: "Градация расхода",
    open: "Показать легенду",
    collapse: "Свернуть легенду",
    low: "менее 500 кг/ч",
    moderate: "500 – 1 000 кг/ч",
    high: "1 000 – 2 000 кг/ч",
    severe: "более 2 000 кг/ч",
    unknown: "нет количественной оценки",
    filterHint: "Клик по строке — показать только эту категорию",
    showAll: "Показать все",
  },
} as const;

/**
 * Literal string types widened back to `string`, structure kept as is.
 *
 * `as const` above is what makes the dictionary self-documenting, but it also
 * types every value as the exact Russian text, which a translation can never
 * satisfy. Widening the leaves keeps the part that matters — the set of keys —
 * so `kk`/`en` still fail to compile if one is missing or misspelled.
 */
type Widen<T> = T extends string ? string : { -readonly [K in keyof T]: Widen<T[K]> };

export type Dictionary = Widen<typeof ru>;
